<template>
  <div :class="containerClassName">
    <label :class="$style.label" :for="inputId" v-if="htmlLabel">{{ htmlLabel }}</label>
    <v-select
      ref="select"
      v-bind="$attrs"
      v-on="listeners"
      v-model="selectModel"
      :inputId="inputId"
      :get-option-label="getOptionLabel"
      :label="label"
      @input="null"
      @open="onOpen"
    >
      <template #no-options>Не найдено</template>
      <template #selected-option="option">
        <span :title="getOptionLabel(option)">
          {{ getOptionLabel(option) }}
        </span>
      </template>

      <slot v-for="(_, name) in $slots" :name="name" :slot="name" />
      <template v-for="(_, name) in $scopedSlots" :slot="name" slot-scope="slotData">
        <slot :name="name" v-bind="slotData" />
      </template>
    </v-select>
    <div v-if="dangerText" :class="$style.errorText">{{ dangerText }}</div>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core';

const POPPER_OPTIONS = {
  placement: "bottom-start",
  modifiers: [],
};

export default {
  name:  "ElsaVSelect",
  props: {
    id:             {
      type: String,
    },
    htmlLabel:      {
      type: String,
    },
    danger:         {
      type: Boolean,
    },
    dangerText:     {
      type: String,
    },
    value:          {},
    styleType:      {
      type: [String, Array],
    },
    label:          {
      type:    String,
      default: 'label',
    },
    getOptionLabel: {
      type: Function,
      default( option ) {
        if (typeof option === 'object') {
          if (!option.hasOwnProperty(this.label)) {
            return console.warn(
              `[vue-select warn]: Label key "option.${this.label}" does not` +
              ` exist in options object ${JSON.stringify(option)}.\n` +
              'https://vue-select.org/api/props.html#getoptionlabel',
            );
          }
          return option[this.label];
        }
        return option;
      },
    },
  },
  data() {
    return {
      popper: null,
    };
  },
  computed: {
    containerClassName() {
      const styleTypes = Array.isArray(this.styleType) ? this.styleType : [this.styleType];
      return {
        [this.$style.container]:  true,
        [this.$style.error]:      this.danger,
        [this.$style.small]:      styleTypes.includes('small'),
        [this.$style.largeLabel]: styleTypes.includes('largeLabel'),
        [this.$style.square]:     styleTypes.includes('square'),
        [this.$style.noBorder]:   styleTypes.includes('noBorder'),
        [this.$style.multiline]:  styleTypes.includes('multiline'),
      };
    },
    inputId() { return '' + (this.id || this._uid); },
    selectModel: {
      get() {
        return this.value;
      },
      set( v ) {
        this.$emit('input', v);
        this.$emit('update:danger', false);
      },
    },
    listeners() {
      return { ...this.$listeners, input: null };
    },
  },
  methods:  {
    focus() {
      this.$refs.select.$el.querySelector('.vs__search').focus();
    },

    onOpen() {
      if (this.$listeners.open) this.$listeners.open();
      requestAnimationFrame(this.createPopper);
    },

    createPopper() {
      this.destroyPopper();
      this.popper = createPopper(
        this.$refs.select.$el,
        this.$refs.select.$refs.dropdownMenu,
        POPPER_OPTIONS
      );
    },

    destroyPopper() {
      if (this.popper) {
        this.popper.destroy();
        this.popper = null;
      }
    },
  },
  beforeDestroy() {
    this.destroyPopper();
  },
};
</script>

<style module lang="scss">
.label {
  display: block;
  margin-bottom: 2px;
  padding-left: 5px;
  font-size: .85rem;
  color: rgba(var(--app-heading-color-light), 1);
}

.errorText {
  position: relative;
  font-size: .65rem;
  overflow: hidden;
  transition: all .25s ease;
  color: rgba(var(--vs-danger), 1);
  padding: 2px 4px 4px;
  display: none;
}

.container :global {
  .vs__selected {
    //font-size: 1rem;
    //white-space: nowrap;
  }

  .vs__dropdown-toggle, .vs--open .vs__dropdown-toggle {
    border-radius: 5px;
    border-color: rgba(0, 0, 0, .4);
    padding: 0;
  }

  :not(.vs--disabled) {
    .vs__dropdown-toggle, .vs--open .vs__dropdown-toggle {
      background: #ffffff;
    }
  }

  .vs__selected-options {
    padding: 10px 0 10px 10px;
    min-width: 0;
    flex-wrap: nowrap;
  }

  .vs__selected {
    display: block;
    margin: 0 4px 0 0;
    padding: 0px 14px 0 4px;
    border: 0;
    line-height: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100%;
    position: relative;

    & .vs__deselect {
      position: absolute;
      right: 0;
      top: 1px;
    }
  }

  .vs--single .vs__selected {
    margin: 0;
    padding: 0;
  }

  .vs__search, .vs__search:focus {
    margin: 0;
    padding: 0;
    border: 0;
    //font-size: 1rem;
    font-size: .95rem;
    font-family: inherit;
    line-height: 1;
  }

  .vs__actions {
    padding: 0 5px;
  }

  // todo: убрать !important
  .vs__dropdown-menu {
    margin-top: 4px !important;
    padding: 6px;
    box-shadow: 0 5px 20px 0 rgba(0, 0, 0, .05);
    border-radius: 5px;
    background: #fff;
  }

  .vs__dropdown-option {
    white-space: normal;
    font-size: 1rem;
    padding: 5px !important;
  }

  .feather-icon {
    display: block;
  }

  .feather {
    display: block;
    width: 13px;
    height: 13px;
  }
}

.error {
  .label {
    color: rgba(var(--vs-danger), 1);
  }

  .errorText {
    display: block;
  }

  :global {
    .vs__dropdown-toggle {
      border-color: rgba(var(--vs-danger), 1);
    }
  }
}

.small {
  :global {
    .vs__selected-options {
      padding: 7px 0 7px 7px;
      min-width: 0;
      flex-wrap: nowrap;
      align-items: baseline;
    }
  }
}

.largeLabel {
  .label {
    font-size: 1rem;
    padding-left: 0;
  }
}

.square {
  :global {
    .vs__dropdown-toggle, .vs--open .vs__dropdown-toggle {
      border-radius: 2px;
    }
  }
}

.noBorder {
  :global {
    .vs__dropdown-toggle, .vs--open .vs__dropdown-toggle {
      border-radius: 0px;
      border: 0;
    }
  }
}

.multiline {
  :global {
    .vs__selected-options {
      flex-wrap: wrap;
      min-height: 36px;
      padding: 9px 0 9px 8px;
    }

    .vs__selected {
      margin: 2px;
    }
  }
}
</style>
